(function () {
  'use strict';

  var VIDEO_SRC = 'https://cdn.shopify.com/videos/c/o/v/170d390674ec4468b2f00c9c916e806d.mp4';
  var INJECTED_ATTR = 'data-dd-video-injected';

  // TEMPORARY diagnostic logging — remove once the variant-change bug is
  // confirmed fixed. Filter DevTools console by "[dd-video]" to isolate.
  function dbg() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[dd-video]');
    console.log.apply(console, args);
  }

  // ── Rondell injection (VSlide / gravity-software.com) ─────────────────────
  // Rondell positions every item absolutely with inline styles, computed for
  // its original item count. Appending our thumb shifts the row off-center,
  // so we recompute a centered `left` for every thumbnail (incl. ours) any
  // time the row changes — including after a click rotates the carousel.

  // Thumb geometry, frozen after the first successful measurement (see
  // layoutRow below for why this must NOT be re-derived from the DOM on
  // every call).
  var rowGap = null;
  var thumbW = null;
  var thumbH = null;
  var thumbTop = null;

  function findRondellContainer() {
    return document.querySelector('.rondell-container');
  }

  // The gallery observer (declared below) also watches style-attribute
  // changes, since rondell repositions its real thumbnails in place on
  // variant change (no add/remove). layoutRow() writes styles too — pausing
  // the observer around our own writes stops that from being an infinite
  // mutation → relayout → mutation loop, while still catching rondell's own.
  function pauseGalleryObserver() {
    if (galleryObserver) galleryObserver.disconnect();
  }

  function resumeGalleryObserver() {
    if (galleryObserver && galleryTarget) {
      galleryObserver.observe(galleryTarget, GALLERY_OBSERVER_OPTIONS);
    }
  }

  // `offset` shifts the whole row left (like a scroll position) — used so the
  // thumbnail strip can be dragged horizontally without touching the hero image,
  // which lives in the same container but is untouched by this function.
  function layoutRow(container, offset) {
    var thumbs = Array.prototype.slice.call(
      container.querySelectorAll('a.rondell-item:not(.rondell-item-focused)')
    );
    var ours = container.querySelector('[' + INJECTED_ATTR + ']');
    dbg('layoutRow() called — realThumbs:', thumbs.length, 'oursFound:', !!ours, 'offset:', offset);
    if (!thumbs.length) { dbg('layoutRow() bailing — no real thumbs found'); return; }

    thumbs.sort(function (a, b) {
      return (parseInt(a.style.left, 10) || 0) - (parseInt(b.style.left, 10) || 0);
    });

    // Measure width/height/top/gap only once, from the first call ever made
    // (i.e. before this function has written anything). Rondell's native
    // thumb size and gap are constant regardless of which item is focused —
    // re-deriving them from the live DOM on every call is what caused the
    // variant-change gap bug: this function also WRITES thumbs' `left`, so a
    // later read can catch either (a) our own previous write, feeding back
    // into itself, or (b) rondell mid-transition (it can take 500ms+ to
    // settle a click, longer than the 50/350ms re-layout timers below), which
    // has thumbnails at transient/wrong positions or a stale focused item
    // still holding hero-sized dimensions. Either way a single bad read used
    // to get baked in permanently. Freezing the geometry here makes that
    // impossible — only `containerWidth` and `thumbs.length` are re-read live,
    // and both self-correct on the next call since nothing derived from them
    // is cached.
    if (thumbW === null) {
      var first = thumbs[0];
      thumbW = parseInt(first.style.width, 10) || 70;
      thumbH = parseInt(first.style.height, 10) || 70;
      thumbTop = parseInt(first.style.top, 10) || 0;
      if (thumbs.length > 1) {
        var l0 = parseInt(thumbs[0].style.left, 10) || 0;
        var l1 = parseInt(thumbs[1].style.left, 10) || 0;
        var gap = (l1 - l0) - thumbW;
        if (gap >= 0) rowGap = gap;
      }
    }
    var w = thumbW, h = thumbH, top = thumbTop;
    var gapPx = rowGap === null ? 2 : rowGap;

    var containerWidth = container.offsetWidth || parseInt(container.style.width, 10) || 0;
    var totalCount = thumbs.length + (ours ? 1 : 0);
    var rowWidth = totalCount * w + (totalCount - 1) * gapPx;
    var maxOffset = Math.max(0, rowWidth - containerWidth);
    var startLeft = Math.max(0, Math.round((containerWidth - rowWidth) / 2));

    offset = Math.max(0, Math.min(offset || 0, maxOffset));

    pauseGalleryObserver();

    // "ours" goes first (slot 0); every real thumb shifts over by one slot.
    var oursSlot = ours ? 1 : 0;
    thumbs.forEach(function (el, i) {
      el.style.left = (startLeft + (i + oursSlot) * (w + gapPx) - offset) + 'px';
    });

    if (ours) {
      ours.style.left = (startLeft - offset) + 'px';
      ours.style.top = top + 'px';
      ours.style.width = w + 'px';
      ours.style.height = h + 'px';
      var crop = ours.querySelector('.dd-video-thumb-crop');
      var img = ours.querySelector('img');
      if (crop) { crop.style.width = w + 'px'; crop.style.height = h + 'px'; }
      if (img) {
        img.style.width = w + 'px';
        img.style.height = h + 'px';
        img.style.maxWidth = w + 'px';
        img.style.maxHeight = h + 'px';
      }
    }

    container._ddRow = { top: top, h: h, offset: offset, maxOffset: maxOffset };

    resumeGalleryObserver();

    if (ours) {
      var oursRect = ours.getBoundingClientRect();
      var containerRect = container.getBoundingClientRect();
      dbg('layoutRow() positioned ours at left/top:', ours.style.left, ours.style.top,
        '— viewport rect:', JSON.stringify(oursRect),
        '— container rect:', JSON.stringify(containerRect),
        '— computed display/opacity:', getComputedStyle(ours).display, getComputedStyle(ours).opacity);
    }
  }

  // Custom horizontal drag scoped to the thumbnail row's vertical band only —
  // deliberately not native `overflow-x:auto` on the container, since that
  // element also holds the full-size hero image and would scroll it too.
  function enableThumbDrag(container) {
    var drag = null;

    function inBand(clientY) {
      var row = container._ddRow;
      if (!row) return false;
      var rect = container.getBoundingClientRect();
      var y = clientY - rect.top;
      return y >= row.top - 10 && y <= row.top + row.h + 10;
    }

    container.addEventListener('touchstart', function (e) {
      var row = container._ddRow;
      var t = e.touches[0];
      if (!row || row.maxOffset <= 0 || !inBand(t.clientY)) { drag = null; return; }
      drag = { startX: t.clientX, startY: t.clientY, startOffset: row.offset, axis: null };
    }, { passive: true });

    container.addEventListener('touchmove', function (e) {
      if (!drag) return;
      var t = e.touches[0];
      var dx = t.clientX - drag.startX;
      var dy = t.clientY - drag.startY;
      if (drag.axis === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (drag.axis === 'x') {
        if (e.cancelable) e.preventDefault();
        layoutRow(container, drag.startOffset - dx);
      }
    }, { passive: false });

    container.addEventListener('touchend', function () { drag = null; }, { passive: true });
  }

  function buildRondellThumb(w, h, top) {
    // Deliberately NOT using the "rondell-item" class: rondell's own arrow
    // navigation likely re-queries elements by that class, and giving our
    // thumb the same class risks it becoming a real navigable "slide" that
    // could get promoted to the main hero image. Everything visual is
    // inlined below, so we don't need the class for styling anyway.
    var item = document.createElement('a');
    item.className = 'dd-video-thumb';
    item.setAttribute(INJECTED_ATTR, '');
    item.href = '#';
    item.style.cssText =
      'position:absolute;display:block;cursor:pointer;opacity:0.8;' +
      'width:' + w + 'px;height:' + h + 'px;top:' + top + 'px;left:0px;';

    var crop = document.createElement('div');
    crop.className = 'dd-video-thumb-crop';
    crop.style.cssText = 'width:' + w + 'px;height:' + h + 'px;overflow:hidden;';

    var img = document.createElement('img');
    img.src = window.ddVideoThumbUrl || '';
    img.alt = 'Watch product video';
    img.className = 'dd-video-thumb-image';
    img.style.cssText =
      'width:' + w + 'px;height:' + h + 'px;max-width:' + w + 'px;max-height:' + h + 'px;' +
      'object-fit:cover;display:block;margin:0;';

    crop.appendChild(img);
    item.appendChild(crop);
    item.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
    return item;
  }

  function injectIntoRondell(container) {
    dbg('injectIntoRondell() called, container:', container);
    if (container.querySelector('[' + INJECTED_ATTR + ']')) { dbg('injectIntoRondell() bailing — already injected'); return; }

    var thumbs = container.querySelectorAll('a.rondell-item:not(.rondell-item-focused)');
    if (!thumbs.length) { dbg('injectIntoRondell() bailing — no real thumbs found'); return; }

    var w = parseInt(thumbs[0].style.width, 10) || 70;
    var h = parseInt(thumbs[0].style.height, 10) || 70;
    var top = parseInt(thumbs[0].style.top, 10) || 0;

    // Insert as the first child so DOM/tab order matches the visual "first
    // thumbnail" position (not just its absolute-positioned left offset).
    container.insertBefore(buildRondellThumb(w, h, top), container.firstChild);
    layoutRow(container, 0);
    enableThumbDrag(container);

    // Rondell rotates the carousel on thumbnail click, reassigning which
    // item is "focused" — that changes the set/order of thumbs, so re-center.
    container.addEventListener('click', function (e) {
      if (e.target.closest('[' + INJECTED_ATTR + ']')) return;
      setTimeout(function () { layoutRow(container, 0); }, 50);
      setTimeout(function () { layoutRow(container, 0); }, 350);
    });
  }

  // ── Generic fallback (non-rondell galleries) ───────────────────────────────

  var THUMB_LIST_SELECTORS = [
    '[class*="vslide"] ul',
    '[class*="vslide"] [class*="thumb"]',
    '.vslide-thumbnails',
    '.vslide__thumb-list',
    '.product__media-list',
  ];

  function findThumbList() {
    for (var i = 0; i < THUMB_LIST_SELECTORS.length; i++) {
      var el = document.querySelector(THUMB_LIST_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  function buildGenericThumb(list) {
    var tag = (list.tagName === 'UL') ? 'li' : 'div';
    var item = document.createElement(tag);
    item.setAttribute(INJECTED_ATTR, '');
    item.style.cssText = 'cursor:pointer;list-style:none;';
    var img = document.createElement('img');
    img.src = window.ddVideoThumbUrl || '';
    img.alt = 'Watch product video';
    img.style.cssText = 'width:100%;height:auto;display:block;';
    item.appendChild(img);
    item.addEventListener('click', openModal);
    return item;
  }

  function isInjected() {
    return !!document.querySelector('[' + INJECTED_ATTR + ']');
  }

  function inject() {
    if (isInjected()) return;
    var rondell = findRondellContainer();
    if (rondell) {
      injectIntoRondell(rondell);
      return;
    }
    var list = findThumbList();
    if (list) list.appendChild(buildGenericThumb(list));
  }

  // Retry up to 20 times (10s) — VSlide loads from an external CDN
  function tryInject(attempts) {
    // Lets an A/B test (e.g. Intelligems) opt a variation out of the video
    // thumbnail entirely, by setting this flag anytime before/during the
    // retry window below. Gating here (rather than only inside inject())
    // means a disabled variation never injects AND never attaches the
    // gallery MutationObserver, so there's no ongoing re-injection attempt
    // to fight against — the thumbnail simply never exists, so Rondell's
    // native layout (no reserved slot) renders untouched.
    if (window.ddVideoDisableThumbnail) return;
    attempts = attempts || 0;
    if (attempts > 20) return;
    if (isInjected()) return;
    var rondell = findRondellContainer();
    var ready = rondell && rondell.querySelector('a.rondell-item:not(.rondell-item-focused)');
    if (!ready) {
      setTimeout(function () { tryInject(attempts + 1); }, 500);
      return;
    }
    inject();
    observe();
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  var modal = null;

  function buildModal() {
    var el = document.createElement('div');
    el.id = 'dd-vm';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product video');
    el.innerHTML =
      '<div class="dd-vm__overlay"></div>' +
      '<div class="dd-vm__box">' +
        '<button class="dd-vm__close" aria-label="Close video">' +
          '<svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<line x1="1" y1="1" x2="17" y2="17" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
          '<line x1="17" y1="1" x2="1" y2="17" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
        '<video class="dd-vm__video" src="' + VIDEO_SRC + '" controls playsinline preload="metadata"></video>' +
      '</div>';

    // Delegate "click outside" to the modal root itself rather than relying
    // on the overlay div's geometry — robust regardless of stacking/inset quirks.
    el.addEventListener('click', function (e) {
      if (!e.target.closest('.dd-vm__box')) closeModal();
    });
    el.querySelector('.dd-vm__close').addEventListener('click', closeModal);
    document.body.appendChild(el);
    return el;
  }

  function openModal() {
    if (!modal) modal = buildModal();
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var video = modal.querySelector('video');
    if (video) video.play().catch(function () {});
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    var video = modal.querySelector('video');
    if (video) video.pause(); // keep playhead so reopening resumes where it left off
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  // ── Styles ─────────────────────────────────────────────────────────────────

  function addStyles() {
    var s = document.createElement('style');
    s.textContent =
      '#dd-vm{display:none;position:fixed;inset:0;z-index:9999;align-items:center;justify-content:center;' +
        'padding:16px;background:rgba(0,0,0,0.85) !important;}' +
      '#dd-vm.is-open{display:flex;}' +
      '.dd-vm__overlay{position:absolute;inset:0;background:rgba(0,0,0,0.85) !important;}' +
      '.dd-vm__box{position:relative;z-index:1;width:100%;max-width:960px;background:#0a0a0a;' +
        'padding:24px;border-radius:12px;box-sizing:border-box;box-shadow:0 20px 60px rgba(0,0,0,0.5);}' +
      '.dd-vm__video{width:100%;max-height:75vh;display:block;border-radius:6px;background:#000;}' +
      '.dd-vm__close{position:absolute;top:12px;right:12px;background:#000;border:none;' +
        'border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;' +
        'cursor:pointer;color:#fff;transition:background 0.15s;z-index:2;}' +
      '.dd-vm__close:hover{background:#2a2a2a;}';
    document.head.appendChild(s);
  }

  // ── Observer — re-sync after variant changes update the gallery ────────────

  var galleryObserver = null;
  var galleryTarget = null;
  // childList only, deliberately no attribute watching here: we're observing
  // document.body as a catch-all, and attribute-level watching at that scope
  // would fire on every unrelated inline-style tweak anywhere on the page
  // (other scripts/animations), constantly resetting the debounce timer.
  var GALLERY_OBSERVER_OPTIONS = { childList: true, subtree: true };

  function observe() {
    // NOTE: previously this watched #rondellCarousel directly, but on this
    // site rondell replaces that whole element (not just its children) when
    // the variant changes — a MutationObserver bound to a specific node never
    // hears about that node itself being swapped out by its parent. Observing
    // document.body guarantees we catch the replacement regardless of which
    // ancestor performs the swap.
    var target = document.body;
    if (!target) return;

    galleryTarget = target;

    // Disconnect any previous observer so repeated variant changes don't
    // stack up multiple observers all doing the same work.
    if (galleryObserver) galleryObserver.disconnect();

    var debounceTimer = null;
    galleryObserver = new MutationObserver(function (mutations) {
      dbg('MutationObserver fired — record count:', mutations.length,
        'types:', mutations.map(function (m) { return m.type + (m.attributeName ? ':' + m.attributeName : ''); }).join(','));
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var stillInjected = isInjected();
        dbg('debounced handler running — isInjected:', stillInjected);
        if (!stillInjected) {
          // A single delayed retry isn't enough — rondell may take longer
          // than that to finish rebuilding for the new variant (e.g. loading
          // new images), so reuse the same up-to-10s retry loop as initial load.
          tryInject(0);
          return;
        }
        // Our thumb is still there, but rondell may have just repositioned/
        // resized its real thumbnails for the new variant — resync ours to match.
        var rondell = findRondellContainer();
        dbg('resyncing via layoutRow, rondell found:', !!rondell);
        if (rondell) layoutRow(rondell, 0);
      }, 200);
    });
    galleryObserver.observe(target, GALLERY_OBSERVER_OPTIONS);
    dbg('observe() attached to target:', target);
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    addStyles();
    tryInject(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
